import { Editor, Element, Node } from 'slate';

import type { CacheableCMNode } from '../internal';
import {
  clearDefaultFooterCache,
  clearDefaultHeaderCache,
  clearEvenFooterCache,
  clearEvenHeaderCache,
  clearFirstPageFooterCache,
  clearFirstPageHeaderCache,
  doInsideNormalization,
  renderModelCache,
} from '../internal';

/**
 * Note: We do NOT call the original normalizeNode from Slate as we intentionally want to opt out of that default normalization.
 * We still replicate the parts of Slate's normalization applicable to our implementation.
 */
export function withHeaderFooterCacheInvalidation<T extends Editor>(
  editor: T,
  options: { callNextNormalization?: boolean } = {}
): T {
  const { callNextNormalization = false } = options;
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry): void => {
    doInsideNormalization(editor, () => {
      const [node, path] = entry;
      const pathRef = Editor.pathRef(editor, path);

      // Clear the caches for headers and footers when they are changed
      if (Element.isElement(node)) {
        switch (node.type) {
          case 'default-header':
            clearDefaultHeaderCache();
            break;
          case 'first-page-header':
            clearFirstPageHeaderCache();
            break;
          case 'even-header':
            clearEvenHeaderCache();
            break;
          case 'default-footer':
            clearDefaultFooterCache();
            break;
          case 'first-page-footer':
            clearFirstPageFooterCache();
            break;
          case 'even-footer':
            clearEvenFooterCache();
            break;
          default:
            break;
        }
      }

      /**
       * Cache invalidation is necessary to rebuild editor node when page and table dimensions or
       * layout changes. We clear all possible nodes from cache to avoid regression in the future
       * when adding new nodes to the editor
       * Type assertion is safe here and is used instead of a list of type guards
       * It is safe to delete data from weakMap this way. (it returns false if object was not
       * found in the weakMap)
       */
      renderModelCache.delete(node as CacheableCMNode);

      const newPath = pathRef.unref();

      if (callNextNormalization && newPath && Node.has(editor, newPath)) {
        const updatedEntry = Editor.node(editor, newPath);
        normalizeNode(updatedEntry);
      }
    });
  };

  return editor;
}
